// FASE 1.4 — Reusable textarea with @mention autocomplete.
// Detects the current "@token" being typed, fetches /api/shadow/users/search,
// and inserts the chosen username. Notifications are sent server-side.
import React, { useEffect, useRef, useState } from "react";
import { API, safeJson } from "../../lib/api";

const findMentionToken = (text, caret) => {
  // Walk back from caret to find a preceding "@" not followed by whitespace.
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === "@") {
      // Ensure char before "@" is whitespace or start (avoid emails / handles inside words).
      const prev = i > 0 ? text[i - 1] : "";
      if (prev && !/\s/.test(prev)) return null;
      const token = text.slice(i + 1, caret);
      if (!/^[a-zA-Z0-9_]*$/.test(token)) return null;
      return { start: i, token };
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
};

export const MentionTextarea = ({
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
  className = "",
  dataTestid,
  ...rest
}) => {
  const ref = useRef(null);
  const [mention, setMention] = useState(null); // { start, token, results, active }
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!mention || mention.token.length < 1) {
        if (!cancelled) setSuggestions([]);
        return;
      }
      try {
        const res = await fetch(`${API}/shadow/users/search?q=${encodeURIComponent(mention.token)}&limit=5`);
        const data = await safeJson(res);
        if (cancelled || !res.ok) return;
        setSuggestions(data?.users || []);
        setActive(0);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    };
    const t = setTimeout(run, 120);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mention?.token]);

  const handleChange = (e) => {
    const next = e.target.value;
    onChange(next);
    const caret = e.target.selectionStart || next.length;
    setMention(findMentionToken(next, caret));
  };

  const insertMention = (username) => {
    if (!mention || !ref.current) return;
    const before = value.slice(0, mention.start);
    const after = value.slice(ref.current.selectionStart || value.length);
    const inserted = `@${username} `;
    const next = `${before}${inserted}${after}`;
    onChange(maxLength ? next.slice(0, maxLength) : next);
    setMention(null);
    setSuggestions([]);
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = (before + inserted).length;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      const choice = suggestions[active];
      if (choice?.username) {
        e.preventDefault();
        insertMention(choice.username);
      }
    } else if (e.key === "Escape") {
      setMention(null);
      setSuggestions([]);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={className}
        data-testid={dataTestid}
        {...rest}
      />
      {suggestions.length > 0 && (
        <div
          className="absolute z-20 mt-1 max-h-56 w-64 overflow-y-auto rounded-xl border border-white/10 bg-zinc-950/95 shadow-[0_8px_28px_rgba(0,0,0,0.45)] backdrop-blur"
          data-testid="mention-dropdown"
        >
          {suggestions.map((u, idx) => (
            <button
              key={u.id}
              type="button"
              onClick={() => insertMention(u.username)}
              onMouseEnter={() => setActive(idx)}
              data-testid={`mention-suggestion-${u.username}`}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                idx === active ? "bg-amber-400/[0.12] text-amber-200" : "text-white/75 hover:bg-white/[0.05]"
              }`}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-[10px] font-black text-black">
                {(u.name || u.username || "?").trim().charAt(0).toUpperCase()}
              </span>
              <span className="flex flex-col min-w-0">
                <span className="truncate font-mono font-bold">@{u.username}</span>
                <span className="truncate text-[10px] text-white/45">{u.name}</span>
              </span>
              {u.is_pro && (
                <span className="ml-auto rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-px text-[8px] font-black tracking-widest text-amber-200">
                  PRO
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
